/**
 * FaqManagementPage.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * [배경/목적]
 * 관리자용 FAQ 관리 페이지
 * FAQ의 등록, 수정, 삭제, 순서 변경 기능 제공
 * 
 * [주요 기능]
 * - FAQ CRUD (생성, 조회, 수정, 삭제)
 * - DataGrid를 통한 목록 관리
 * - Slide Panel Editor로 편집
 * - 실시간 프리뷰
 * - 드래그 앤 드롭 순서 변경
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Drawer,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  CircularProgress,
  Fade,
  Slide,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  DragIndicator as DragIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import {
  getAllFAQs,
  createFAQ,
  updateFAQ,
  deleteFAQ,
  toggleFAQActive,
  getNextOrder,
} from '../../services/faqService';
import type { FAQ, FAQInput } from '../../types/faq.types';
import { Timestamp } from 'firebase/firestore';

// 페이드인 애니메이션
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// 폼 초기값
const INITIAL_FORM: FAQInput = {
  question: '',
  answer: '',
  order: 1,
  isActive: true,
};

export default function FaqManagementPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // 상태 관리
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 편집 패널 상태
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [formData, setFormData] = useState<FAQInput>(INITIAL_FORM);

  // 삭제 다이얼로그 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<FAQ | null>(null);

  // 스낵바 상태
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // 프리뷰 확장 상태
  const [previewExpanded, setPreviewExpanded] = useState(false);

  // FAQ 목록 로드
  const loadFAQs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllFAQs({ activeOnly: false, sortOrder: 'asc' });
      setFaqs(data);
    } catch (err) {
      console.error('FAQ 로드 실패:', err);
      setError('FAQ 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFAQs();
  }, [loadFAQs]);

  // 새 FAQ 추가 버튼 클릭
  const handleAddNew = async () => {
    const nextOrder = await getNextOrder();
    setFormData({ ...INITIAL_FORM, order: nextOrder });
    setEditingFaq(null);
    setEditorOpen(true);
  };

  // 수정 버튼 클릭
  const handleEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      order: faq.order,
      isActive: faq.isActive,
    });
    setEditorOpen(true);
  };

  // 삭제 버튼 클릭
  const handleDeleteClick = (faq: FAQ) => {
    setFaqToDelete(faq);
    setDeleteDialogOpen(true);
  };

  // 삭제 확인
  const handleDeleteConfirm = async () => {
    if (!faqToDelete) return;

    try {
      setSaving(true);
      await deleteFAQ(faqToDelete.id);
      setSnackbar({
        open: true,
        message: 'FAQ가 삭제되었습니다.',
        severity: 'success',
      });
      loadFAQs();
    } catch (err) {
      setSnackbar({
        open: true,
        message: '삭제에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
      setFaqToDelete(null);
    }
  };

  // 활성화 상태 토글
  const handleToggleActive = async (faq: FAQ) => {
    try {
      await toggleFAQActive(faq.id, !faq.isActive);
      setSnackbar({
        open: true,
        message: faq.isActive ? 'FAQ가 비활성화되었습니다.' : 'FAQ가 활성화되었습니다.',
        severity: 'info',
      });
      loadFAQs();
    } catch (err) {
      setSnackbar({
        open: true,
        message: '상태 변경에 실패했습니다.',
        severity: 'error',
      });
    }
  };

  // 폼 저장
  const handleSave = async () => {
    // 유효성 검사
    if (!formData.question.trim()) {
      setSnackbar({
        open: true,
        message: '질문을 입력해주세요.',
        severity: 'error',
      });
      return;
    }
    if (!formData.answer.trim()) {
      setSnackbar({
        open: true,
        message: '답변을 입력해주세요.',
        severity: 'error',
      });
      return;
    }

    try {
      setSaving(true);

      if (editingFaq) {
        // 수정
        await updateFAQ(editingFaq.id, formData);
        setSnackbar({
          open: true,
          message: 'FAQ가 수정되었습니다.',
          severity: 'success',
        });
      } else {
        // 생성
        await createFAQ(formData);
        setSnackbar({
          open: true,
          message: 'FAQ가 등록되었습니다.',
          severity: 'success',
        });
      }

      setEditorOpen(false);
      loadFAQs();
    } catch (err) {
      setSnackbar({
        open: true,
        message: '저장에 실패했습니다.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // 에디터 닫기
  const handleCloseEditor = () => {
    setEditorOpen(false);
    setEditingFaq(null);
    setFormData(INITIAL_FORM);
  };

  // DataGrid 컬럼 정의
  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: '순서',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            minWidth: 32,
            bgcolor: '#e3f2fd',
            color: '#1976d2',
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      field: 'question',
      headerName: '질문',
      flex: 1,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'answer',
      headerName: '답변',
      flex: 1,
      minWidth: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            fontSize: 13,
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: '상태',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? '활성' : '비활성'}
          size="small"
          color={params.value ? 'success' : 'default'}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: 'updated_at',
      headerName: '수정일',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value instanceof Timestamp
          ? params.value.toDate()
          : new Date(params.value);
        return (
          <Typography variant="caption" color="text.secondary">
            {date.toLocaleDateString('ko-KR')}
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: '관리',
      width: 160,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const faq = params.row as FAQ;
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={faq.isActive ? '비활성화' : '활성화'}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleActive(faq);
                }}
                sx={{ color: faq.isActive ? '#4caf50' : '#9e9e9e' }}
              >
                {faq.isActive ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tooltip title="수정">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(faq);
                }}
                sx={{ color: '#1976d2' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="삭제">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(faq);
                }}
                sx={{ color: '#f44336' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // 에디터 패널
  const editorPanel = (
    <Box
      sx={{
        width: isMobile ? '100%' : 480,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fafbfc',
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HelpIcon sx={{ color: '#667eea' }} />
          <Typography variant="h6" fontWeight={700}>
            {editingFaq ? 'FAQ 수정' : 'FAQ 등록'}
          </Typography>
        </Box>
        <IconButton onClick={handleCloseEditor} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* 폼 영역 */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {/* 기본 정보 */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2.5,
            bgcolor: '#fff',
            border: '1px solid #e0e7ef',
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 2 }}>
            기본 정보
          </Typography>

          <TextField
            fullWidth
            label="질문 *"
            value={formData.question}
            onChange={(e) => setFormData({ ...formData, question: e.target.value })}
            placeholder="예: 매입 신청은 어떻게 하나요?"
            sx={{ mb: 2.5 }}
            InputProps={{
              sx: { bgcolor: '#fff' },
            }}
          />

          <TextField
            fullWidth
            label="답변 *"
            value={formData.answer}
            onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
            placeholder="답변 내용을 입력하세요..."
            multiline
            rows={6}
            sx={{ mb: 2.5 }}
            InputProps={{
              sx: { bgcolor: '#fff' },
            }}
          />

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              type="number"
              label="순서"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
              sx={{ width: 100 }}
              InputProps={{
                inputProps: { min: 1 },
                sx: { bgcolor: '#fff' },
              }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="success"
                />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  활성화
                </Typography>
              }
            />
          </Box>
        </Paper>

        {/* 실시간 프리뷰 */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: '#fff',
            border: '1px solid #e0e7ef',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 1.5,
              bgcolor: '#f5f7fa',
              borderBottom: '1px solid #e0e7ef',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PreviewIcon sx={{ fontSize: 18, color: '#667eea' }} />
              <Typography variant="subtitle2" fontWeight={700}>
                미리보기
              </Typography>
            </Box>
            <Chip label="실시간" size="small" color="info" sx={{ height: 20 }} />
          </Box>

          <Box sx={{ p: 2 }}>
            {formData.question || formData.answer ? (
              <Accordion
                expanded={previewExpanded}
                onChange={() => setPreviewExpanded(!previewExpanded)}
                elevation={0}
                sx={{
                  border: '1px solid #667eea',
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#667eea' }} />}
                  sx={{
                    bgcolor: 'rgba(102, 126, 234, 0.04)',
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Typography
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#667eea',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      Q
                    </Typography>
                    <Typography fontWeight={600} sx={{ color: '#667eea', fontSize: 15 }}>
                      {formData.question || '질문을 입력해주세요'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: '#fafbfc', borderTop: '1px solid #f0f0f0', p: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Typography
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#764ba2',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      A
                    </Typography>
                    <Typography sx={{ color: '#555', fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      {formData.answer || '답변을 입력해주세요'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Box
                sx={{
                  py: 4,
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                <HelpIcon sx={{ fontSize: 32, mb: 1, opacity: 0.3 }} />
                <Typography variant="body2">
                  질문과 답변을 입력하면 미리보기가 표시됩니다.
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      {/* 하단 버튼 */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: '#fff',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCloseEditor}
          fullWidth
          sx={{ py: 1.2 }}
        >
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          fullWidth
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{
            py: 1.2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
            },
          }}
        >
          {saving ? '저장 중...' : editingFaq ? '수정' : '등록'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        animation: `${fadeIn} 0.4s ease-out`,
        pb: 4,
      }}
    >
      {/* 페이지 헤더 */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          color: '#fff',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HelpIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
                FAQ 관리
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                자주 묻는 질문을 등록하고 관리합니다
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={loadFAQs}
              startIcon={<RefreshIcon />}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              새로고침
            </Button>
            <Button
              variant="contained"
              onClick={handleAddNew}
              startIcon={<AddIcon />}
              sx={{
                bgcolor: '#fff',
                color: '#667eea',
                fontWeight: 700,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              FAQ 등록
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 통계 카드 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card
          elevation={0}
          sx={{
            flex: '1 1 200px',
            border: '1px solid #e0e7ef',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              전체 FAQ
            </Typography>
            <Typography variant="h4" fontWeight={800} color="primary">
              {faqs.length}
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: '1 1 200px',
            border: '1px solid #e0e7ef',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              활성 FAQ
            </Typography>
            <Typography variant="h4" fontWeight={800} color="success.main">
              {faqs.filter((f) => f.isActive).length}
            </Typography>
          </CardContent>
        </Card>
        <Card
          elevation={0}
          sx={{
            flex: '1 1 200px',
            border: '1px solid #e0e7ef',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              비활성 FAQ
            </Typography>
            <Typography variant="h4" fontWeight={800} color="text.secondary">
              {faqs.filter((f) => !f.isActive).length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 에러 알림 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* FAQ 목록 테이블 */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #e0e7ef',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            bgcolor: '#f5f7fa',
            borderBottom: '1px solid #e0e7ef',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            FAQ 목록
          </Typography>
        </Box>
        <DataGrid
          rows={faqs}
          columns={columns}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: 'order', sort: 'asc' }],
            },
          }}
          onRowClick={(params: GridRowParams) => handleEdit(params.row as FAQ)}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderColor: '#f0f0f0',
              py: 1.5,
            },
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#fafbfc',
              borderColor: '#e0e7ef',
            },
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
              '&:hover': {
                bgcolor: '#f8f9ff',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: '#e0e7ef',
            },
          }}
          localeText={{
            noRowsLabel: 'FAQ가 없습니다',
            MuiTablePagination: {
              labelRowsPerPage: '표시 개수:',
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} / ${count !== -1 ? count : `${to}+`}`,
            },
          }}
        />
      </Paper>

      {/* 편집 드로어 */}
      <Drawer
        anchor="right"
        open={editorOpen}
        onClose={handleCloseEditor}
        PaperProps={{
          sx: {
            width: isMobile ? '100%' : 480,
            boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        {editorPanel}
      </Drawer>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: 2, maxWidth: 400 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            <Typography fontWeight={700}>FAQ 삭제</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            이 FAQ를 삭제하시겠습니까?
            <Box component="span" sx={{ display: 'block', mt: 1, fontWeight: 600, color: 'text.primary' }}>
              "{faqToDelete?.question}"
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 1, color: 'error.main', fontSize: 13 }}>
              삭제된 데이터는 복구할 수 없습니다.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined">
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={saving}
            startIcon={saving && <CircularProgress size={16} color="inherit" />}
          >
            {saving ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 알림 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ minWidth: 280 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
